// =============================================================================
// Profile slice — Phase 2 hydration lifecycle.
//
// `hydration` is a one-shot bootstrap axis, separate from the per-request
// `status` field. Transitions are dispatched ONLY by bootstrapApp():
//
//   idle → pending → success                (auth present, fetch ok)
//   idle → pending → failed                 (auth present, fetch failed)
//   idle → unauthenticated                  (no auth at bootstrap)
//
// -----------------------------------------------------------------------------
// STALE-CACHE CONTRACT — read this before consuming `profile.data`.
// -----------------------------------------------------------------------------
// `profile.data` BEFORE `hydration === 'success'` is OPTIMISTIC STALE CACHE
// ONLY. It comes from the legacy localStorage.userInfo blob via
// projectProfileFromLegacy() at preloadedState time. It MAY be:
//   - arbitrarily out of date (last persisted value, possibly hours/days old)
//   - inconsistent with the live backend state for the same identity
//   - inconsistent with state.auth identity fields during the mismatch
//     window (see bootstrapActions.js for diagram)
//   - null entirely (logged-out cold-start, OR fresh sign-up without persist)
//
// Treat `profile.data` as authoritative ONLY when:
//   - `hydration === 'success'`, OR
//   - the consumer explicitly opts into stale-tolerance (e.g. presentational
//     fallback while a fresh fetch is in flight).
//
// Null-tolerance: `data` MAY remain null even after `hydration === 'success'`
// is unreachable (e.g. authenticated but profile fetch failed and no
// preloadedState seed existed). Every consumer must null-check `data` before
// reading fields.
// -----------------------------------------------------------------------------
//
// Phase 4 will add per-mutation handlers (PROFILE_UPDATE_SUCCESS, PROFILE_RESET).
// Phase 6 makes this slice canonical; until then, state.userLogin.userInfo
// remains source of truth and consumers should use compat selectors.
// =============================================================================

import {
  PROFILE_HYDRATION_PENDING,
  PROFILE_HYDRATION_SUCCESS,
  PROFILE_HYDRATION_FAILED,
  PROFILE_HYDRATION_UNAUTHENTICATED,
} from '../constants/profileConstants';

const INITIAL_PROFILE_STATE = {
  data: null,
  status: 'idle',
  error: null,
  hydration: 'idle',
};

export const profileReducers = (state = INITIAL_PROFILE_STATE, action) => {
  switch (action.type) {
    case PROFILE_HYDRATION_PENDING:
      return { ...state, hydration: 'pending', error: null };

    case PROFILE_HYDRATION_SUCCESS:
      return {
        ...state,
        hydration: 'success',
        data: action.payload,
        error: null,
      };

    case PROFILE_HYDRATION_FAILED:
      // Keep last-known data (from preloadedState seed) — consumers decide
      // whether to render stale data with a warning or treat as null.
      return { ...state, hydration: 'failed', error: action.payload };

    case PROFILE_HYDRATION_UNAUTHENTICATED:
      return {
        ...state,
        hydration: 'unauthenticated',
        data: null,
        error: null,
      };

    default:
      return state;
  }
};
